RivalIQ.apps.AdsInsights.module('views', function (views, App, Backbone, Marionette, $, _, s, moment) {

    'use strict';

    const TargetingInfoView = RivalIQ.views.ItemView.extend({
        className: 'ad-targeting-info',
        template: '#adTargetingInfo',

        loadData: function () {
            const me = this;

            const fetchAttrs = {
                adAccountId: me.getOption('adAccountId'),
                adObjectId: me.getOption('adObjectId')
            };

            return RivalIQ.apps.AdsInsights.data.adObjectTargeting.fetch(fetchAttrs)
                .then(function (adTargetingInfo) {
                    me.adTargetingInfo = adTargetingInfo;
                });
        },

        serializeData: function () {
            const me = this;

            return {
                adTargetingInfo: me.adTargetingInfo
            };
        }
    });

    const AdObjectDetail = RivalIQ.views.ItemView.extend({
        template: '#adObjectDetail',
        className: 'col-md-6 ad-object-detail'
    });

    function titleizeEnum(value) {
        return s.titleize(value.replace('_', ' '));
    }

    function enumName(enums, value) {
        let name = _.get(enums, [value, 'name']);
        if (!name) {
            name = titleizeEnum(value);
        }
        return name;
    }

    const AdObjectStatusDetail = AdObjectDetail.extend({
        template: '#adObjectStatusDetail',

        serializeData: function () {
            const me = this;

            const status = me.model.get('detailValue');
            const statusName = enumName(RivalIQ.enums.facebookAdDeliveryStatuses, status);
            return {
                status: status,
                statusName: statusName
            };
        }
    });

    const AdObjectBidDetail = AdObjectDetail.extend({
        template: '#adObjectBidDetail',

        serializeData: function () {
            const me = this;

            let bidTypeDisplayValue = null;
            const bidType = _.get(me.model.get('adObject'), 'bid_type');
            if (bidType) {
                bidTypeDisplayValue = RivalIQ.strings[`facebook_ads_insights_bid_type_${bidType.toLowerCase()}`];
                if (!bidTypeDisplayValue) {
                    bidTypeDisplayValue = titleizeEnum(bidType);
                }
            }

            return _.defaults({
                bidTypeDisplayValue: bidTypeDisplayValue
            }, me.model.toJSON());
        }
    });

    const AdObjectAdLabels = RivalIQ.views.ItemView.extend({
        template: '#adObjectLabels',
        className: 'col-md-12 ad-object-detail'
    });

    const AdObjectObjectiveDetail = AdObjectDetail.extend({
        serializeData: function () {
            const me = this;

            const detailValue = me.model.get('detailValue');

            return _.defaults({
                detailValue: enumName(RivalIQ.enums.facebookAdCampaignObjectives, detailValue)
            }, me.model.toJSON());
        }
    });

    const AdObjectDate = AdObjectDetail.extend({
        serializeData: function () {
            const me = this;

            const detailValue = me.model.get('detailValue');
            const timezone = me.model.get('timezone');
            let formattedDetailValue;
            if (timezone) {
                const timezoneName = RivalIQ.utils.timezone.getTimezoneAbbreviation(timezone, detailValue);
                formattedDetailValue = `${RivalIQ.utils.timezone.getDateInTimezone(timezone, detailValue).format('LL LTS')} ${timezoneName}`;
            } else {
                formattedDetailValue = moment.utc(detailValue).format('LL LTS');
            }

            return _.defaults({
                detailValue: formattedDetailValue
            }, me.model.toJSON());
        }
    });

    const AdObjectDetails = Marionette.CollectionView.extend({
        getChildView: function (item) {
            return item.get('view') || AdObjectDetail;
        }
    });

    function hasTargetingInfo(adObjectType) {
        return _.includes([RivalIQ.enums.facebookAdObjectTypes.ADSET, RivalIQ.enums.facebookAdObjectTypes.AD], adObjectType);
    }

    function formatSubUnitCurrency(value) {
        if (value) {
            value = _.defaults({
                value: value.value / 100
            }, value);
        }
        return formatCurrency(value);
    }

    function formatCurrency(value) {
        // facebook returns 0 when it means null for these budgets.
        if (!value || value.value === 0) {
            return '--';
        }
        return RivalIQ.formatter.formatAmountAndCurrency(value);
    }

    function createCampaignDetailsCollection(adObject) {

        return new Backbone.Collection([
            {
                detailName: 'Campaign Status',
                detailValue: adObject.effective_status,
                view: AdObjectStatusDetail
            },
            {
                detailName: 'Objective',
                detailValue: adObject.objective,
                view: AdObjectObjectiveDetail
            },

            {
                detailName: 'Spend Cap',
                detailValue: formatSubUnitCurrency(adObject.spend_cap)
            },
            {
                detailName: 'Buying Type',
                detailValue: s.titleize(adObject.buying_type)
            },

            {
                detailName: 'Campaign Created At',
                detailValue: adObject.created_time,
                timezone: adObject.timezone,
                view: AdObjectDate
            },
            {
                detailName: 'Campaign Last Updated At',
                detailValue: adObject.updated_time,
                timezone: adObject.timezone,
                view: AdObjectDate
            },

            {
                detailName: 'Labels',
                adlabels: adObject.adlabels || [],
                view: AdObjectAdLabels
            }
        ]);
    }

    function createAdSetDetailsCollection(adObject) {

        let frequencyCap = 'None';
        const frequencyControlSpecs = _.head(adObject.frequency_control_specs);
        if (frequencyControlSpecs) {
            const intervalDays = frequencyControlSpecs.interval_days || 1;
            const maxFrequency = frequencyControlSpecs.max_frequency || 1;

            frequencyCap = `${maxFrequency}/${intervalDays} impressions/day`;
        }

        return new Backbone.Collection([
            {
                detailName: 'Adset Status',
                detailValue: adObject.effective_status,
                view: AdObjectStatusDetail
            },
            // optimization_goals are the same set as campaign objectives
            {
                detailName: 'Optimization Goal',
                detailValue: adObject.optimization_goal,
                view: AdObjectObjectiveDetail
            },

            {
                detailName: 'Daily Budget',
                detailValue: formatSubUnitCurrency(adObject.daily_budget)
            },
            {
                detailName: 'Lifetime Budget',
                detailValue: formatSubUnitCurrency(adObject.lifetime_budget)
            },

            {
                detailName: 'Frequency Cap',
                detailValue: frequencyCap
            },
            {
                detailName: 'Bid Details',
                adObject: adObject,
                view: AdObjectBidDetail
            },

            {
                detailName: 'Ad Set Created At',
                detailValue: adObject.created_time,
                timezone: adObject.timezone,
                view: AdObjectDate
            },
            {
                detailName: 'Ad Set Last Updated At',
                detailValue: adObject.updated_time,
                timezone: adObject.timezone,
                view: AdObjectDate
            }
        ]);
    }

    function createAdDetailsCollection(adObject) {

        return new Backbone.Collection([
            {
                detailName: 'Ad Status',
                detailValue: adObject.effective_status,
                view: AdObjectStatusDetail
            },
            {
                detailName: 'Bid Details',
                adObject: adObject,
                view: AdObjectBidDetail
            },
            {
                detailName: 'Ad Created At',
                detailValue: adObject.created_time,
                timezone: adObject.timezone,
                view: AdObjectDate
            },
            {
                detailName: 'Ad Last Updated At',
                detailValue: adObject.updated_time,
                timezone: adObject.timezone,
                view: AdObjectDate
            }
        ]);
    }

    function createDetailsCollection(adObject) {

        switch (adObject.adObjectType) {
            case RivalIQ.enums.facebookAdObjectTypes.CAMPAIGN:
                return createCampaignDetailsCollection(adObject);
            case RivalIQ.enums.facebookAdObjectTypes.ADSET:
                return createAdSetDetailsCollection(adObject);
            case RivalIQ.enums.facebookAdObjectTypes.AD:
                return createAdDetailsCollection(adObject);
            default:
                throw new Error(`unknown adObjectType ${adObject.adObjectType}`);
        }
    }

    views.AdObjectDetailsModal = RivalIQ.views.LayoutView.mixin({
        template: '#adObjectDetailsModal',
        regions: {
            // body is used by the backdrop modal as the container to look for height changes.
            body: '.x-ad-targeting-info',
            detailsContainer: '.x-details-container'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props,
            modalBackdropResizing: RivalIQ.apps.Shell.views.ModalBackdropResizing
        },

        className: 'modal',
        attributes: {
            tabindex: -1
        },

        initialize: function () {
            const me = this;

            me.adObject = _.extend(me.getOption('adObject'), {
                timezone: _.get(me.dashboardModel.getCuration(), ['facebookAdAccount', 'timezone'])
            });
        },

        serializeData: function () {
            const me = this;

            return {
                adObject: me.adObject,
                editLink: App.utils.constructNativeLink(me.adObject, true)
            };
        },

        templateHelpers: function () {
            return _.extend({
                s: s
            }, RivalIQ.utils.emoji.getTemplateHelpers());
        },

        onBeforeRenderLoaded: function () {
            const me = this;

            if (hasTargetingInfo(me.adObject.adObjectType)) {
                me.body.show(new TargetingInfoView({
                    adAccountId: me.getProp('adAccountId'),
                    adObjectId: me.adObject.id
                }));
            }
        },

        onRender: function () {
            const me = this;

            const collection = createDetailsCollection(me.adObject);
            me.detailsContainer.show(new AdObjectDetails({
                collection: collection
            }));
        },

        onShown: function () {
            const me = this;

            RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.V3ViewedFacebookAdsAdObjectDetailsModal, {
                adObjectType: me.adObject.adObjectType
            });

            RivalIQ.apps.Shell.views.ModalBackdropResizing.onShown.apply(me, arguments);
        }
    });
}, window.s, window.moment);
